import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import rolesService from "../services/rolesService";
import styles from "../assets/roles.module.css";

const RoleEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [role, setRole] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState([]);

  useEffect(() => {
    // Prevent editing role ID 1 (admin)
    if (parseInt(id) === 1) {
      toast.error("Cannot edit admin role");
      navigate('/roles');
      return;
    }
    
    loadRole();
  }, [id, navigate]);

  const loadRole = async () => {
    try {
      setLoading(true);
      const response = await rolesService.getRoleById(id);
      
      if (response.success) {
        setRole(response.data.role);
        setPermissions(response.data.permissions);
        setSelectedPermissions(response.data.rolePerms || []);
      } else {
        throw new Error(response.message || 'Failed to load role');
      }
    } catch (error) {
      console.error("Error loading role:", error);
      toast.error("Failed to load role. Using mock data.");
      
      // Fallback to mock data
      const mockResponse = {
        role: rolesService.getMockRolesData().find(r => r.id === parseInt(id)),
        permissions: rolesService.getMockPermissionsData(),
        rolePerms: rolesService.getMockRolesData().find(r => r.id === parseInt(id))?.permissions.map(p => p.id) || []
      };
      
      if (mockResponse.role) {
        setRole(mockResponse.role);
        setPermissions(mockResponse.permissions);
        setSelectedPermissions(mockResponse.rolePerms);
      } else {
        toast.error("Role not found");
        navigate('/roles');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionChange = (permissionId) => {
    setSelectedPermissions(prev => {
      if (prev.includes(permissionId)) {
        return prev.filter(id => id !== permissionId);
      } else {
        return [...prev, permissionId];
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErrors([]);

    try {
      const formData = {
        perms: selectedPermissions
      };

      const response = await rolesService.updateRole(id, formData);

      if (response.success) {
        toast.success("Role successfully updated");
        navigate('/roles');
      } else {
        throw new Error(response.message || 'Failed to update role');
      }
    } catch (error) {
      console.error("Error updating role:", error);
      setErrors([error.message || 'Failed to update role']);
      toast.error(error.message || 'Failed to update role');
    } finally {
      setSaving(false);
    }
  };

  if (loading && !role) {
    return (
      <div className={styles.container}>
        <div className={styles.row}>
          <div className={styles.colMd12}>
            <div className={styles.panel + ' ' + styles.panelDefault + ' ' + styles.panelStandard}>
              <div className={styles.panelBody + ' ' + styles.textCenter}>
                <div className={styles.spinnerBorder} role="status">
                  <span className={styles.srOnly}>Loading...</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!role) {
    return null;
  }

  return (
    <div className={styles.container}>
      {/* Breadcrumb */}
      <nav className={styles.breadcrumb}>
        <Link to="/dashboard" className={styles.breadcrumbItem}>
          Dashboard
        </Link>
        <span> {' / '} </span>
        <span className={styles.menuHighlight}>Menu</span>
        <span> {' / '} </span>
        <Link to="/roles" className={styles.breadcrumbItem}>
          Roles
        </Link>
        <span> {' / '} </span>
        <span className={styles.breadcrumbActive}>Edit Role</span>
      </nav>

      <div className={styles.row}>
        <div className={styles.colMd12}>
          {errors.length > 0 && (
            <div className={styles.alert + ' ' + styles.alertDanger}>
              <strong>Whoops!</strong> There were some problems with your input.<br /><br />
              <ul>
                {errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          <div className={styles.panel + ' ' + styles.panelDefault + ' ' + styles.panelStandard}>
            <div className={styles.panelHeading + ' ' + styles.panelTitle}>
              <h3>System Roles</h3>
            </div>
            <div className={styles.panelBody}>
              <form onSubmit={handleSubmit}>
                <input type="hidden" name="name" value={role.name} />
                <input type="hidden" name="display_name" value={role.display_name} />
                <input type="hidden" name="description" value={role.description} />
                <input type="hidden" name="level" value={role.level} />

                <div className={styles.colMd12}>
                  <div className={styles.colMd6}>
                    <dl className={styles.dlHorizontal}>
                      <dt>Name</dt>
                      <dd>{role.name}</dd>
                      <dt>Display name</dt>
                      <dd>{role.display_name}</dd>
                      <dt>Description</dt>
                      <dd>{role.description}</dd>
                      <dt>Level</dt>
                      <dd>{role.level}</dd>
                    </dl>
                  </div>
                  <div className={styles.colMd6}>
                    <div className={styles.formGroup}>
                      <label htmlFor="">Permissions</label>

                      {permissions.map((permission) => (
                        <div key={permission.id} className={styles.checkbox}>
                          <label>
                            <input
                              type="checkbox"
                              name="perms[]"
                              value={permission.id}
                              checked={selectedPermissions.includes(permission.id)}
                              onChange={() => handlePermissionChange(permission.id)}
                            />
                            {permission.display_name}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className={styles.colSm12} style={{ marginBottom: '30px' }}></div>
                  <div className={styles.formGroup + ' ' + styles.textRight}>
                    <div className={styles.colSm5}></div>
                    <div className={styles.colSm5}>
                      <button 
                        type="submit" 
                        className={styles.btn + ' ' + styles.btnHead + ' ' + styles.btnStandard}
                        disabled={saving}
                      >
                        <i className={styles.glyphicon + ' ' + styles.glyphiconOk}></i> 
                        {saving ? " Saving..." : " Save role"}
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoleEdit; 